import { useMemo } from 'react';
import { TravelExpenseReport, TravelPerDiem } from '../Types/admin/timereport';
import {
	CreateTravelReportRequest,
	UpdateTravelReportRequest,
	CreateDriverLogRequest,
	CreateDriverLogItemRequest,
	UpdateDriverLogRequest,
	UpdateDriverLogItemRequest,
} from '../Types/timereport';
import { useBizMutation, useBizQuery } from './useBizQuery';
import { useGetActivities, useGetLeaveSettings } from './useTimereport';
import {
	getTravelReports,
	getTravelReport,
	createTravelReport,
	updateTravelReport,
	deleteTravelReport,
	submitTravelReport,
	getTravelReportTypes,
	getExpenseReports,
	getExpenseReport,
	createExpenseReport,
	updateExpenseReport,
	deleteExpenseReport,
	getPerDiems,
	createPerDiem,
	getDriverLogs,
	getDriverLog,
	createDriverLog,
	updateDriverLog,
	deleteDriverLog,
	submitDriverLog,
	getDriverLogItems,
	createDriverLogItem,
	updateDriverLogItem,
	deleteDriverLogItem,
	getTravelExpenseSettings,
	getExpenseFeatureSettings,
} from '../Services/travelReport';
// Query key constants
const QUERYKEY_TRAVEL_REPORTS = 'TRAVEL_REPORTS';
const QUERYKEY_TRAVEL_REPORT = 'TRAVEL_REPORT';
const QUERYKEY_TRAVEL_REPORT_TYPES = 'TRAVEL_REPORT_TYPES';
const QUERYKEY_EXPENSE_REPORTS = 'EXPENSE_REPORTS';
const QUERYKEY_EXPENSE_REPORT = 'EXPENSE_REPORT';
const QUERYKEY_PER_DIEMS = 'PER_DIEMS';
const QUERYKEY_DRIVER_LOGS = 'DRIVER_LOGS';
const QUERYKEY_DRIVER_LOG = 'DRIVER_LOG';
const QUERYKEY_DRIVER_LOG_ITEMS = 'DRIVER_LOG_ITEMS';
const QUERYKEY_TRAVEL_EXPENSE_SETTINGS = 'TRAVEL_EXPENSE_SETTINGS';
const QUERYKEY_EXPENSE_FEATURE_SETTINGS = 'EXPENSE_FEATURE_SETTINGS';

// Travel Reports

export const useGetExpenseProjectOptions = () => {
	const { activities, statusState: activitiesStatus } = useGetActivities();
	const { leaveSettings, statusState: leaveSettingsStatus } = useGetLeaveSettings();
	const isLoading = [activitiesStatus, leaveSettingsStatus].some((status) => status === 'INITIAL' || status === 'LOADING');

	const projectOptions = useMemo(() => {
		if (!activities || !leaveSettings || activitiesStatus !== 'SUCCESS' || leaveSettingsStatus !== 'SUCCESS') return [];

		const specialProjectIds = new Set([
			leaveSettings.paidVacation?.projectId,
			leaveSettings.unpaidVacation?.projectId,
			leaveSettings.advanceVacation?.projectId,
			leaveSettings.sickLeave?.projectId,
			leaveSettings.parentalLeave?.projectId,
			leaveSettings.leaveOfAbsence?.projectId,
			leaveSettings.savedHours?.projectId,
		]);
		const projects = new Map<number, { Id: number; Name: string }>();
		for (const activity of activities) {
			if (activity.projectId > 0 && !specialProjectIds.has(activity.projectId) && !projects.has(activity.projectId)) {
				projects.set(activity.projectId, {
					Id: activity.projectId,
					Name: `${activity.customer || '—'} › ${activity.projectName}`,
				});
			}
		}
		return Array.from(projects.values());
	}, [activities, leaveSettings, activitiesStatus, leaveSettingsStatus]);

	return { projectOptions, isLoading };
};

export const useGetTravelReports = (start: string, end: string) => {
	const { data, statusState, refetch } = useBizQuery(
		[QUERYKEY_TRAVEL_REPORTS, start, end],
		() => getTravelReports(start, end),
		!!start && !!end
	);
	return { travelReports: data, statusState, refetch };
};

export const useGetTravelReport = (id: number) => {
	const { data, statusState } = useBizQuery([QUERYKEY_TRAVEL_REPORT, id], () => getTravelReport(id), id > 0);
	return { travelReport: data, statusState };
};

export const useCreateTravelReport = () => {
	const wrapperFn = (params: { data: CreateTravelReportRequest }) => createTravelReport(params.data);
	const { mutate, mutateAsync, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TRAVEL_REPORTS]],
	});
	return {
		create: (data: CreateTravelReportRequest) => mutate({ data }),
		createAsync: (data: CreateTravelReportRequest) => mutateAsync({ data }),
		createdReport: data,
		statusState,
	};
};

export const useUpdateTravelReport = () => {
	const wrapperFn = (params: { data: UpdateTravelReportRequest }) => updateTravelReport(params.data);
	const { mutate, mutateAsync, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TRAVEL_REPORTS], [QUERYKEY_TRAVEL_REPORT]],
	});
	return {
		update: (data: UpdateTravelReportRequest) => mutate({ data }),
		updateAsync: (data: UpdateTravelReportRequest) => mutateAsync({ data }),
		updatedReport: data,
		statusState,
	};
};

export const useDeleteTravelReport = () => {
	const wrapperFn = (params: { id: number }) => deleteTravelReport(params.id);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TRAVEL_REPORTS]],
	});
	return {
		remove: (id: number) => mutate({ id }),
		statusState,
	};
};

export const useSubmitTravelReport = () => {
	const wrapperFn = (params: { id: number }) => submitTravelReport(params.id);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_TRAVEL_REPORTS]],
	});
	return {
		submit: (id: number) => mutate({ id }),
		statusState,
	};
};

// Travel Report Types

export const useGetTravelReportTypes = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_TRAVEL_REPORT_TYPES], getTravelReportTypes);
	return { travelReportTypes: data, statusState };
};

export const useGetTravelExpenseSettings = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_TRAVEL_EXPENSE_SETTINGS], getTravelExpenseSettings);
	return { travelExpenseSettings: data, statusState };
};

export const useGetExpenseFeatureSettings = () => {
	const { data, statusState } = useBizQuery([QUERYKEY_EXPENSE_FEATURE_SETTINGS], getExpenseFeatureSettings);
	return { expenseFeatureSettings: data, statusState };
};

// Expense Reports

export const useGetExpenseReports = () => {
	const { data, statusState, refetch } = useBizQuery([QUERYKEY_EXPENSE_REPORTS], getExpenseReports);
	return { expenseReports: data, statusState, refetch };
};

export const useGetExpenseReport = (id: number) => {
	const { data, statusState } = useBizQuery([QUERYKEY_EXPENSE_REPORT, id], () => getExpenseReport(id), id > 0);
	return { expenseReport: data, statusState };
};

export const useCreateExpenseReport = () => {
	const wrapperFn = (params: {
		report: Omit<TravelExpenseReport, 'id' | 'portalId' | 'userId' | 'isSubmitted' | 'isAttested' | 'isDeleted'>;
	}) => createExpenseReport(params.report);
	const { mutate, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_EXPENSE_REPORTS]],
	});
	return {
		create: (report: Omit<TravelExpenseReport, 'id' | 'portalId' | 'userId' | 'isSubmitted' | 'isAttested' | 'isDeleted'>) =>
			mutate({ report }),
		createdReport: data,
		statusState,
	};
};

export const useUpdateExpenseReport = () => {
	const wrapperFn = (params: { report: TravelExpenseReport }) => updateExpenseReport(params.report);
	const { mutate, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_EXPENSE_REPORTS], [QUERYKEY_EXPENSE_REPORT]],
	});
	return {
		update: (report: TravelExpenseReport) => mutate({ report }),
		updatedReport: data,
		statusState,
	};
};

export const useDeleteExpenseReport = () => {
	const wrapperFn = (params: { id: number }) => deleteExpenseReport(params.id);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_EXPENSE_REPORTS]],
	});
	return {
		remove: (id: number) => mutate({ id }),
		statusState,
	};
};

// Per-Diem

export const useGetPerDiems = (expenseReportId: number) => {
	const { data, statusState } = useBizQuery(
		[QUERYKEY_PER_DIEMS, expenseReportId],
		() => getPerDiems(expenseReportId),
		expenseReportId > 0
	);
	return { perDiems: data, statusState };
};

export const useCreatePerDiem = () => {
	const wrapperFn = (params: { perDiem: Omit<TravelPerDiem, 'id' | 'portalId' | 'isDeleted'> }) =>
		createPerDiem(params.perDiem);
	const { mutate, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_PER_DIEMS]],
	});
	return {
		create: (perDiem: Omit<TravelPerDiem, 'id' | 'portalId' | 'isDeleted'>) => mutate({ perDiem }),
		createdPerDiem: data,
		statusState,
	};
};

// Driver Logs

export const useGetDriverLogs = () => {
	const { data, statusState, refetch } = useBizQuery([QUERYKEY_DRIVER_LOGS], getDriverLogs);
	return { driverLogs: data, statusState, refetch };
};

export const useGetDriverLog = (id: number) => {
	const { data, statusState } = useBizQuery([QUERYKEY_DRIVER_LOG, id], () => getDriverLog(id), id > 0);
	return { driverLog: data, statusState };
};

export const useCreateDriverLog = () => {
	const wrapperFn = (params: { data: CreateDriverLogRequest }) => createDriverLog(params.data);
	const { mutate, mutateAsync, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_DRIVER_LOGS]],
	});
	return {
		create: (data: CreateDriverLogRequest) => mutate({ data }),
		createAsync: (data: CreateDriverLogRequest) => mutateAsync({ data }),
		createdLog: data,
		statusState,
	};
};

export const useUpdateDriverLog = () => {
	const wrapperFn = (params: { data: UpdateDriverLogRequest }) => updateDriverLog(params.data);
	const { mutate, mutateAsync, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_DRIVER_LOGS], [QUERYKEY_DRIVER_LOG]],
	});
	return {
		update: (data: UpdateDriverLogRequest) => mutate({ data }),
		updateAsync: (data: UpdateDriverLogRequest) => mutateAsync({ data }),
		updatedLog: data,
		statusState,
	};
};

export const useDeleteDriverLog = () => {
	const wrapperFn = (params: { id: number }) => deleteDriverLog(params.id);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_DRIVER_LOGS]],
	});
	return {
		remove: (id: number) => mutate({ id }),
		statusState,
	};
};

export const useSubmitDriverLog = () => {
	const wrapperFn = (params: { id: number }) => submitDriverLog(params.id);
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_DRIVER_LOGS]],
	});
	return {
		submit: (id: number) => mutate({ id }),
		statusState,
	};
};

export const useGetDriverLogItems = (driverLogId: number) => {
	const { data, statusState, refetch } = useBizQuery(
		[QUERYKEY_DRIVER_LOG_ITEMS, driverLogId],
		() => getDriverLogItems(driverLogId),
		driverLogId > 0
	);
	return { driverLogItems: data, statusState, refetch };
};

export const useCreateDriverLogItem = () => {
	const wrapperFn = (params: { data: CreateDriverLogItemRequest }) => createDriverLogItem(params.data);
	const { mutate, mutateAsync, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: (params) => [[QUERYKEY_DRIVER_LOG_ITEMS, params.data.driverLogId], [QUERYKEY_DRIVER_LOGS]],
	});
	return {
		create: (data: CreateDriverLogItemRequest) => mutate({ data }),
		createAsync: (data: CreateDriverLogItemRequest) => mutateAsync({ data }),
		createdDriverLogItem: data,
		statusState,
	};
};

export const useUpdateDriverLogItem = () => {
	const wrapperFn = (params: { data: UpdateDriverLogItemRequest }) => updateDriverLogItem(params.data);
	const { mutate, mutateAsync, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: (params) => [[QUERYKEY_DRIVER_LOG_ITEMS, params.data.driverLogId], [QUERYKEY_DRIVER_LOGS]],
	});
	return {
		update: (data: UpdateDriverLogItemRequest) => mutate({ data }),
		updateAsync: (data: UpdateDriverLogItemRequest) => mutateAsync({ data }),
		statusState,
	};
};

export const useDeleteDriverLogItem = () => {
	const wrapperFn = (params: { id: number }) => deleteDriverLogItem(params.id);
	const { mutate, mutateAsync, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: (params: { id: number; driverLogId: number }) => [
			[QUERYKEY_DRIVER_LOG_ITEMS, params.driverLogId],
			[QUERYKEY_DRIVER_LOGS],
		],
	});
	return {
		remove: (id: number, driverLogId: number) => mutate({ id, driverLogId }),
		removeAsync: (id: number, driverLogId: number) => mutateAsync({ id, driverLogId }),
		statusState,
	};
};
